import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getStep, initClient, submitStep } from '@/api/agency-quote-api';
import { SceneData } from '@/schemas/sceneDataSchema';
import { AuthResult, PartnerBrandingConfig } from '@/schemas/clientInitSchema';
import { decodeSubmission } from '@/schemas/validation/ValidationSchemaGenerator';
import { FormValues } from '@/constants/formTypes';
import { currentPathName, getRouteStep, setRoute } from '@/services/location-service';
import AnalyticsContext, { AnalyticsContextType } from '@/contexts/analytics/analytics-context';
import { trackViewEvent, trackEvent, EventProperties } from '@/services/analytics-service';
import { pathContext } from './use-analytics';
import { reportError, trackUser } from '@/services/error-reporting-service';
import AuthService from '@/services/auth-service';
import { ApiError } from '@/constants/errorTypes';
import { datadogRum } from '@datadog/browser-rum';
import { ErrorResponse } from '@/schemas/errorResponseSchema';
import sessionId from '@/services/session-service';
import { applyStepData, mergeStepData } from '@/stores/sync';

function useTracking() {
  const {
    eventPrefix,
    context: { quoteId, agentId, publicId, quoteSource },
    setContext,
  } = useContext<AnalyticsContextType>(AnalyticsContext);

  const trackScene = useCallback(
    (
      { step_id, entity_index, experience, experiments, quote_id, agent_id, market }: SceneData,
      overridePublicId?: string
    ) => {
      const pathName = setRoute({
        step_id,
        entity_index,
      });

      const eventProperties: EventProperties = {
        eventPrefix,
        agentId: agent_id ?? agentId,
        quoteId: quote_id ?? quoteId,
        publicId: overridePublicId ?? publicId, // Use override if provided
        experience,
        quoteSource,
      };

      setContext((context) => (quote_id ? { ...context, quoteId: quote_id, agentId: agent_id, experience } : context));
      updateRumSession({ quote_id, agent_id, market, experience, experiments });
      trackViewed(pathName, eventProperties, quote_id, agent_id, overridePublicId ?? publicId);
    },
    [eventPrefix, quoteId, agentId, publicId, quoteSource, setContext]
  );

  const trackInit = useCallback(
    ({
      quote_id,
      agent_id,
      public_id,
      quote_source,
      experience = null,
    }: AuthResult & { experience: string | null }) => {
      const currentQuoteId = quote_id ?? quoteId;
      const currentPublicId = public_id ?? publicId;
      const currentAgentId = agent_id ?? agentId;
      const currentQuoteSource = quote_source ?? quoteSource;

      setContext((context) => ({
        quoteId: quote_id ?? context.quoteId,
        agentId: agent_id ?? context.agentId,
        publicId: public_id ?? context.publicId,
        experience: experience ?? context.experience,
        quoteSource: quote_source ?? context.quoteSource,
        experiments: {},
      }));

      trackEvent(
        '',
        'INITIALIZED',
        {
          eventPrefix,
          currentPath: currentPathName(),
          quoteId: currentQuoteId,
          agentId: currentAgentId,
          publicId: currentPublicId,
          experience: experience,
          quoteSource: currentQuoteSource,
        },
        currentQuoteId
      );
    },
    [quoteId, agentId, publicId, quoteSource, setContext, eventPrefix]
  );

  return { trackScene, trackInit };
}

function updateRumSession({
  quote_id,
  agent_id,
  market,
  experience,
  experiments,
}: Pick<SceneData, 'quote_id' | 'agent_id' | 'market' | 'experience' | 'experiments'>) {
  datadogRum.setUser({
    ...datadogRum.getUser(),
    id: sessionId(),
    quoteId: quote_id,
    agentId: agent_id,
    market,
    experience,
  });

  Object.entries(experiments).forEach(([key, value]) => {
    datadogRum.addFeatureFlagEvaluation(key, value);
  });
}

function trackViewed(
  pathName: string,
  eventProperties: EventProperties,
  quoteId: string | null,
  agentId: string | null,
  publicId: string | null
) {
  trackViewEvent(pathContext(pathName), { ...eventProperties, publicId, agentId }, quoteId);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'pageview',
    page: pathName,
  });
}

export type FlowStatus = 'initializing' | 'loading' | 'ready' | 'error';

export const useFlow = () => {
  const initLoading = useRef(false);
  const sceneDataRef = useRef<SceneData | undefined>(undefined);
  const { trackScene, trackInit } = useTracking();
  const [flowStatus, setFlowStatus] = useState<FlowStatus>('initializing');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorData, setErrorData] = useState<ErrorResponse | undefined>();
  const [errorStatus, setErrorStatus] = useState<number | undefined>();

  const intervalRef = useRef<number | null>(null);
  const requestNumberRef = useRef<number>(0);

  const [sceneData, setSceneData] = useState<SceneData>();
  const [pollingNumber, setPollingNumber] = useState(0);
  const [partnerBrandingConfig, setPartnerBrandingConfig] = useState<PartnerBrandingConfig>();

  const fireSceneEventCommunicator = ({ event, payload }: { event: string; payload: Record<string, string> }) => {
    if (!window.ReactNativeWebView?.postMessage) {
      return;
    }

    window.ReactNativeWebView.postMessage(JSON.stringify({ event, payload }));
  };

  const handleError = useCallback((error: unknown) => {
    reportError({ error });

    if (error instanceof ApiError && error.errorData) {
      setErrorData(error.errorData);
      setErrorStatus(error.status);
    } else {
      setErrorData(undefined);
      setErrorStatus(undefined);
    }

    setFlowStatus('error');
  }, []);

  // Ref avoids stale closure in fetchStep when reading current sceneData.
  useEffect(() => {
    sceneDataRef.current = sceneData;
  });

  const fetchStep: RouteFn = useCallback(
    async (entity_index, step_id) => {
      try {
        const isRefresh = entity_index === undefined && step_id === undefined;
        const current = sceneDataRef.current;

        setIsRefreshing(isRefresh);
        setFlowStatus('loading');

        // When both are undefined (REFRESH), the server re-evaluates flow
        // conditions and decides which step to return. When explicit values
        // are passed (ROUTE), the server returns that specific step.
        const data = await getStep(entity_index, step_id);
        trackScene(data);
        setSceneData(data);

        // Same-scene refresh merges to preserve pending field changes.
        const isSameScene =
          isRefresh && current?.step_id === data.step_id && current?.entity_index === data.entity_index;

        if (isSameScene) {
          mergeStepData(data.step_state);
        } else {
          applyStepData(data.step_state);
        }

        setIsRefreshing(false);
        setFlowStatus('ready');
      } catch (error) {
        setIsRefreshing(false);
        handleError(error);
      }
    },
    [handleError, trackScene]
  );

  const pollForRequestNumber = useCallback(
    async (requestNumber: number) => {
      try {
        const data = await getStep(sceneData?.entity_index, sceneData?.step_id);

        // if we're at the end of a flow don't set state
        // If our request number has changed throw away result
        if (requestNumber != requestNumberRef.current) {
          return;
        }

        setPollingNumber((prev) => prev + 1);
        if (JSON.stringify(data) !== JSON.stringify(sceneData)) {
          // Reset polling if the scene actually changes
          if (data.step_id != sceneData?.step_id || data.entity_index != sceneData.entity_index) {
            setPollingNumber(0);
          }

          setSceneData(data);
          applyStepData(data.step_state);
          trackScene(data);
        }
      } catch (error) {
        handleError(error);
      }
    },
    [sceneData, trackScene, handleError]
  );

  const init = useCallback(async () => {
    try {
      if (initLoading.current) {
        return;
      }

      initLoading.current = true;
      setFlowStatus('loading');

      const { auth_result, configuration } = await initClient();
      AuthService.setAccessToken(auth_result.access_token);

      trackUser(auth_result);
      setPartnerBrandingConfig(configuration);

      const { entity_index, step_id } = getRouteStep();

      // Fetch the step data
      const data = await getStep(entity_index, step_id);
      trackInit({ ...auth_result, experience: data.experience ?? null });

      // Call trackScene with the fresh publicId from auth_result
      trackScene(data, auth_result.public_id);

      setSceneData(data);
      applyStepData(data.step_state);
      setFlowStatus('ready');
      initLoading.current = false;
    } catch (error) {
      initLoading.current = false;
      handleError(error);
    }
  }, [handleError, trackInit, trackScene]);

  const submit = useCallback(
    async (submissionData: Record<string, unknown>) => {
      if (!sceneData) {
        return;
      }

      try {
        setFlowStatus('loading');

        const data = await submitStep(decodeSubmission(submissionData), sceneData.entity_index, sceneData.step_id);

        setSceneData(data);
        applyStepData(data.step_state);
        trackScene(data);
        setFlowStatus('ready');
      } catch (error) {
        handleError(error);
      }
    },
    [handleError, sceneData, trackScene]
  );

  // Handle event communicator for mobile webview
  useEffect(() => {
    if (sceneData?.scene?.event_communicator) {
      fireSceneEventCommunicator({
        event: sceneData.scene.event_communicator.event,
        payload: sceneData.scene.event_communicator.payload,
      });
    }
  }, [sceneData]);

  // Handle polling
  useEffect(() => {
    requestNumberRef.current += 1;

    // if our form data updates clear the timeout
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }

    const pollingRate = sceneData?.scene?.polling_rate;
    const pollingTimeout = sceneData?.scene?.polling_timeout;
    const validPollRate = !!pollingRate && pollingRate > 0;
    const canPoll =
      typeof pollingRate === 'number' && typeof pollingTimeout === 'number' && pollingTimeout > 0
        ? pollingNumber * pollingRate < pollingTimeout
        : true;

    if (validPollRate && canPoll) {
      intervalRef.current = window.setTimeout(() => void pollForRequestNumber(requestNumberRef.current), pollingRate);
    }

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [sceneData, pollForRequestNumber, pollingNumber]);

  // Handle scene redirects
  useEffect(() => {
    const redirect_to = sceneData?.scene.redirect_to;

    if (redirect_to) {
      try {
        const url = new URL(redirect_to);
        window.location.assign(url.href);
      } catch (error) {
        reportError({ error });
      }
    }
  }, [sceneData]);

  // Handle routing
  useEffect(() => {
    const handler = () => {
      const { entity_index, step_id } = getRouteStep();
      void fetchStep(entity_index, step_id);
    };

    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [fetchStep]);

  return {
    sceneData,
    partnerBrandingConfig,
    flowStatus,
    isRefreshing,
    errorData,
    errorStatus,
    init,
    submit,
    route: fetchStep,
  };
};

export type RouteFn = (entity_index?: number, step_id?: string) => Promise<void>;
export type SubmitFn = (data: FormValues) => void | Promise<void>;
